import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { AuditLogEntry, EnrollmentMode, Paged, StudentWithClient } from '@repo/shared-types';
import type { CreateStudentInput, UpdateStudentInput } from '@repo/validation';
import { toast } from 'sonner';
import { invalidateGettingStarted } from './useGettingStarted';

interface ListOpts {
  search?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'all';
  clientId?: string;
  tags?: string[];
  subjectIds?: string[];
  modes?: EnrollmentMode[];
  noEnrollment?: boolean;
  noSlot?: boolean;
  /** THI-267 Task 15: Lớp filter — same `?classIds=` CSV the students list
   *  route already accepts (Task 6), folded into the same `enrollments.some`
   *  clause as `subjectIds`/`modes`/`noSlot`. */
  classIds?: string[];
  sort?: 'newest' | 'oldest' | 'name' | 'payer';
  page?: number;
  pageSize?: number;
  branchId?: string;
}

function buildUrl(opts?: ListOpts): string {
  const p = new URLSearchParams();
  if (opts?.search) p.set('search', opts.search);
  if (opts?.status) p.set('status', opts.status);
  if (opts?.clientId) p.set('clientId', opts.clientId);
  if (opts?.tags?.length) p.set('tag', opts.tags.join(','));
  if (opts?.subjectIds?.length) p.set('subject', opts.subjectIds.join(','));
  if (opts?.modes?.length) p.set('mode', opts.modes.join(','));
  if (opts?.noEnrollment) p.set('noEnrollment', '1');
  if (opts?.noSlot) p.set('noSlot', '1');
  if (opts?.classIds?.length) p.set('classIds', opts.classIds.join(','));
  if (opts?.sort) p.set('sort', opts.sort);
  if (opts?.page) p.set('page', String(opts.page));
  if (opts?.pageSize) p.set('pageSize', String(opts.pageSize));
  if (opts?.branchId) p.set('branchId', opts.branchId);
  const qs = p.toString();
  return qs ? `/students?${qs}` : '/students';
}

export function useStudents(opts?: ListOpts, options?: { enabled?: boolean }) {
  return useQuery<Paged<StudentWithClient>>({
    queryKey: [
      'students',
      {
        search: opts?.search ?? null,
        status: opts?.status ?? null,
        clientId: opts?.clientId ?? null,
        tags: opts?.tags ?? null,
        subjectIds: opts?.subjectIds ?? null,
        modes: opts?.modes ?? null,
        noEnrollment: opts?.noEnrollment ?? null,
        noSlot: opts?.noSlot ?? null,
        classIds: opts?.classIds ?? null,
        sort: opts?.sort ?? null,
        page: opts?.page ?? null,
        pageSize: opts?.pageSize ?? null,
        branchId: opts?.branchId ?? null,
      },
    ],
    queryFn: () => fetchApi(buildUrl(opts)),
    // THI-267 Task 13: the AddMembersDialog's "Tạo mới" tab runs one of these
    // per draft row (a debounced name+payer-phone duplicate lookup) and the
    // "Chọn từ danh sách" tab runs one for its search box — both need to stay
    // idle until there's really something to search for, same shape as
    // `useClasses`'s own `options?.enabled` above.
    enabled: options?.enabled ?? true,
  });
}

export function useStudentTags() {
  return useQuery<string[]>({
    queryKey: ['students', 'tags'],
    queryFn: () => fetchApi('/students/tags'),
  });
}

export function useStudent(id: string | null) {
  return useQuery<StudentWithClient>({
    queryKey: ['students', id],
    queryFn: () => fetchApi(`/students/${id}`),
    enabled: id !== null,
  });
}

export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStudentInput) =>
      fetchApi<StudentWithClient>('/students', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      invalidateGettingStarted(qc); // checklist step 3
      toast.success('Đã thêm học viên');
    },
    onError: (e) => toast.error(e.message || 'Không thể thêm học viên'),
  });
}

export function useUpdateStudent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateStudentInput) =>
      fetchApi<StudentWithClient>(`/students/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      qc.invalidateQueries({ queryKey: ['students', id] });
      // Deactivating ends the student's enrollments + leaves slots server-side,
      // so refresh those views too (cards were stale until re-navigation).
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (e) => toast.error(e.message || 'Không thể cập nhật học viên'),
  });
}

export function useStudentAudit(studentId: string | null) {
  return useQuery<AuditLogEntry[]>({
    queryKey: ['students', studentId, 'audit'],
    queryFn: () => fetchApi(`/students/${studentId}/audit`),
    enabled: studentId !== null,
  });
}

export function useDeleteStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: boolean }>(`/students/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      toast.success('Đã xoá học viên');
    },
    onError: (e) => toast.error(e.message || 'Không thể xoá học viên'),
  });
}
